import axios from 'axios';
class ApiClient {
    constructor() {
        this.client = axios.create({
            baseURL: process.env.NODE_ENV === 'production'
                ? 'https://crick.therightcoach.co.in/api'
                : 'http://localhost:5252/api',
            timeout: 10000,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        // Request interceptor
        this.client.interceptors.request.use((config) => {
            // Add auth token if available
            const token = localStorage.getItem('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            // If sending FormData, remove Content-Type header so Axios can set multipart/form-data with boundary
            if (config.data instanceof FormData) {
                delete config.headers['Content-Type'];
            }
            return config;
        }, (error) => Promise.reject(error));
        // Response interceptor
        this.client.interceptors.response.use((response) => response, (error) => {
            console.error('API Error:', error);
            // Extract backend error message if available
            if (error.response?.data?.message) {
                const backendError = new Error(error.response.data.message);
                backendError.name = 'BackendError';
                return Promise.reject(backendError);
            }
            return Promise.reject(error);
        });
    }
    async get(url) {
        const response = await this.client.get(url);
        return response.data;
    }
    async post(url, data) {
        const response = await this.client.post(url, data);
        return response.data;
    }
    async put(url, data) {
        const response = await this.client.put(url, data);
        return response.data;
    }
    async delete(url) {
        const response = await this.client.delete(url);
        return response.data;
    }
    // Academy API methods
    async getAcademies(filters) {
        const params = new URLSearchParams();
        if (filters?.name)
            params.append('name', filters.name);
        if (filters?.city)
            params.append('city', filters.city);
        if (filters?.emailId)
            params.append('emailId', filters.emailId);
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies?${queryString}` : '/academies';
        return this.get(url);
    }
    async getAcademyById(id) {
        return this.get(`/academies/${id}`);
    }
    async createAcademy(academyData) {
        return this.post('/academies', academyData);
    }
    async updateAcademy(id, academyData) {
        return this.put(`/academies/${id}`, academyData);
    }
    async deleteAcademy(id) {
        return this.delete(`/academies/${id}`);
    }
    // Inventory API methods
    async getInventoriesByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.name)
            params.append('name', filters.name);
        if (filters?.active !== undefined)
            params.append('active', filters.active.toString());
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/inventories?${queryString}` : `/academies/${academyId}/inventories`;
        return this.get(url);
    }
    async getInventoryById(academyId, id) {
        return this.get(`/academies/${academyId}/inventories/${id}`);
    }
    async createInventory(academyId, inventoryData) {
        return this.post(`/academies/${academyId}/inventories`, inventoryData);
    }
    async updateInventory(academyId, id, inventoryData) {
        return this.put(`/academies/${academyId}/inventories/${id}`, inventoryData);
    }
    async deleteInventory(academyId, id) {
        return this.delete(`/academies/${academyId}/inventories/${id}`);
    }
    // Activity API methods
    async getActivitiesByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.name)
            params.append('name', filters.name);
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/activities?${queryString}` : `/academies/${academyId}/activities`;
        return this.get(url);
    }
    async getActivityById(academyId, id) {
        return this.get(`/academies/${academyId}/activities/${id}`);
    }
    async createActivity(academyId, activityData) {
        return this.post(`/academies/${academyId}/activities`, activityData);
    }
    async updateActivity(academyId, id, activityData) {
        return this.put(`/academies/${academyId}/activities/${id}`, activityData);
    }
    async deleteActivity(academyId, id) {
        return this.delete(`/academies/${academyId}/activities/${id}`);
    }
    // User API methods
    async getUsersByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.name)
            params.append('name', filters.name);
        if (filters?.phone)
            params.append('phone', filters.phone);
        if (filters?.email)
            params.append('email', filters.email);
        if (filters?.roles && filters.roles.length > 0) {
            filters.roles.forEach(role => params.append('roles', role));
        }
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/users?${queryString}` : `/academies/${academyId}/users`;
        return this.get(url);
    }
    async getUserById(academyId, id) {
        return this.get(`/academies/${academyId}/users/${id}`);
    }
    async createUser(academyId, userData) {
        return this.post(`/academies/${academyId}/users`, userData);
    }
    async updateUser(academyId, id, userData, profilePictureFile) {
        if (profilePictureFile !== undefined) {
            // Use FormData for file upload (or to remove picture)
            const formData = new FormData();
            if (profilePictureFile) {
                formData.append('profilePicture', profilePictureFile);
            }
            else {
                // To remove picture, send empty string
                formData.append('profilePicture', '');
            }
            // Only append userData fields if they are actually provided (not just empty object)
            if (userData && Object.keys(userData).length > 0) {
                if (userData.name !== undefined)
                    formData.append('name', userData.name);
                if (userData.phone !== undefined)
                    formData.append('phone', userData.phone || '');
                if (userData.email !== undefined)
                    formData.append('email', userData.email || '');
                if (userData.roles !== undefined)
                    formData.append('roles', JSON.stringify(userData.roles));
                if (userData.speciality !== undefined)
                    formData.append('speciality', userData.speciality || '');
            }
            // Axios will automatically set Content-Type for FormData with boundary
            // The request interceptor will remove the default JSON Content-Type header
            const response = await this.client.put(`/academies/${academyId}/users/${id}`, formData);
            return response.data;
        }
        else {
            return this.put(`/academies/${academyId}/users/${id}`, userData);
        }
    }
    async deleteUser(academyId, id) {
        return this.delete(`/academies/${academyId}/users/${id}`);
    }
    // Batch API methods
    async getBatchesByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.name)
            params.append('name', filters.name);
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/batches?${queryString}` : `/academies/${academyId}/batches`;
        return this.get(url);
    }
    async getBatchById(academyId, id) {
        return this.get(`/academies/${academyId}/batches/${id}`);
    }
    async createBatch(academyId, batchData) {
        return this.post(`/academies/${academyId}/batches`, batchData);
    }
    async updateBatch(academyId, id, batchData) {
        return this.put(`/academies/${academyId}/batches/${id}`, batchData);
    }
    async deleteBatch(academyId, id) {
        return this.delete(`/academies/${academyId}/batches/${id}`);
    }
    // Batch coach management
    async addCoachesToBatch(academyId, batchId, coachIds) {
        return this.post(`/academies/${academyId}/batches/${batchId}/coaches`, { coachIds });
    }
    async removeCoachesFromBatch(academyId, batchId, coachIds) {
        const response = await this.client.delete(`/academies/${academyId}/batches/${batchId}/coaches`, { data: { coachIds } });
        return response.data;
    }
    // Batch player management
    async addPlayersToBatch(academyId, batchId, playerIds) {
        return this.post(`/academies/${academyId}/batches/${batchId}/players`, { playerIds });
    }
    async removePlayersFromBatch(academyId, batchId, playerIds) {
        const response = await this.client.delete(`/academies/${academyId}/batches/${batchId}/players`, { data: { playerIds } });
        return response.data;
    }
    // Session Template API methods
    async getSessionTemplatesByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.title)
            params.append('title', filters.title);
        if (filters?.coachIds && filters.coachIds.length > 0) {
            filters.coachIds.forEach(id => params.append('coachIds', id));
        }
        if (filters?.activityIds && filters.activityIds.length > 0) {
            filters.activityIds.forEach(id => params.append('activityIds', id));
        }
        if (filters?.inventoryIds && filters.inventoryIds.length > 0) {
            filters.inventoryIds.forEach(id => params.append('inventoryIds', id));
        }
        if (filters?.batchIds && filters.batchIds.length > 0) {
            filters.batchIds.forEach(id => params.append('batchIds', id));
        }
        if (filters?.playerIds && filters.playerIds.length > 0) {
            filters.playerIds.forEach(id => params.append('playerIds', id));
        }
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/session-templates?${queryString}` : `/academies/${academyId}/session-templates`;
        return this.get(url);
    }
    async getSessionTemplateById(academyId, id) {
        return this.get(`/academies/${academyId}/session-templates/${id}`);
    }
    async createSessionTemplate(academyId, templateData) {
        return this.post(`/academies/${academyId}/session-templates`, templateData);
    }
    async updateSessionTemplate(academyId, id, templateData) {
        return this.put(`/academies/${academyId}/session-templates/${id}`, templateData);
    }
    async deleteSessionTemplate(academyId, id) {
        return this.delete(`/academies/${academyId}/session-templates/${id}`);
    }
    // Session API methods
    async getSessionsByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.sessionTemplateId)
            params.append('sessionTemplateId', filters.sessionTemplateId);
        if (filters?.coachIds && filters.coachIds.length > 0) {
            filters.coachIds.forEach(id => params.append('coachIds', id));
        }
        if (filters?.activityIds && filters.activityIds.length > 0) {
            filters.activityIds.forEach(id => params.append('activityIds', id));
        }
        if (filters?.inventoryIds && filters.inventoryIds.length > 0) {
            filters.inventoryIds.forEach(id => params.append('inventoryIds', id));
        }
        if (filters?.batchIds && filters.batchIds.length > 0) {
            filters.batchIds.forEach(id => params.append('batchIds', id));
        }
        if (filters?.playerIds && filters.playerIds.length > 0) {
            filters.playerIds.forEach(id => params.append('playerIds', id));
        }
        if (filters?.startDate)
            params.append('startDate', filters.startDate);
        if (filters?.endDate)
            params.append('endDate', filters.endDate);
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/sessions?${queryString}` : `/academies/${academyId}/sessions`;
        return this.get(url);
    }
    async getSessionById(academyId, id) {
        return this.get(`/academies/${academyId}/sessions/${id}`);
    }
    async createSession(academyId, sessionData) {
        return this.post(`/academies/${academyId}/sessions`, sessionData);
    }
    async updateSession(academyId, id, sessionData) {
        return this.put(`/academies/${academyId}/sessions/${id}`, sessionData);
    }
    async deleteSession(academyId, id) {
        return this.delete(`/academies/${academyId}/sessions/${id}`);
    }
    async markAttendance(academyId, sessionId, userId, attended, markedBy) {
        return this.post(`/academies/${academyId}/sessions/${sessionId}/attendance`, { userId, attended, markedBy });
    }
    async updateAttendanceBulk(academyId, sessionId, attendanceUpdates, markedBy) {
        return this.post(`/academies/${academyId}/sessions/${sessionId}/attendance/bulk`, { attendanceUpdates, markedBy });
    }
    // Assessment API methods
    async getAssessmentsByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.parentId !== undefined) {
            params.append('parentId', filters.parentId === null ? 'null' : filters.parentId);
        }
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/assessments?${queryString}` : `/academies/${academyId}/assessments`;
        return this.get(url);
    }
    async getAssessmentById(academyId, id) {
        return this.get(`/academies/${academyId}/assessments/${id}`);
    }
    async createAssessment(academyId, assessmentData) {
        return this.post(`/academies/${academyId}/assessments`, assessmentData);
    }
    async updateAssessment(academyId, id, assessmentData) {
        return this.put(`/academies/${academyId}/assessments/${id}`, assessmentData);
    }
    async deleteAssessment(academyId, id) {
        return this.delete(`/academies/${academyId}/assessments/${id}`);
    }
    // Assessment Report API methods
    async getAssessmentReportsByAcademyId(academyId, filters) {
        const params = new URLSearchParams();
        if (filters?.playerId)
            params.append('playerId', filters.playerId);
        if (filters?.status)
            params.append('status', filters.status);
        if (filters?.sortBy)
            params.append('sortBy', filters.sortBy);
        if (filters?.sortOrder)
            params.append('sortOrder', filters.sortOrder);
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/assessment-reports?${queryString}` : `/academies/${academyId}/assessment-reports`;
        return this.get(url);
    }
    async getAssessmentReportById(academyId, id) {
        return this.get(`/academies/${academyId}/assessment-reports/${id}`);
    }
    async createAssessmentReport(academyId, reportData) {
        return this.post(`/academies/${academyId}/assessment-reports`, reportData);
    }
    async updateAssessmentReport(academyId, id, reportData) {
        return this.put(`/academies/${academyId}/assessment-reports/${id}`, reportData);
    }
    async deleteAssessmentReport(academyId, id) {
        return this.delete(`/academies/${academyId}/assessment-reports/${id}`);
    }
    // Player Dashboard API methods
    async getPlayersDashboard(academyId, weeks) {
        const params = new URLSearchParams();
        if (weeks !== undefined) {
            params.append('weeks', weeks.toString());
        }
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/players/dashboard?${queryString}` : `/academies/${academyId}/players/dashboard`;
        return this.get(url);
    }
    async getPlayerDetails(academyId, playerId, weeks) {
        const params = new URLSearchParams();
        if (weeks !== undefined) {
            params.append('weeks', weeks.toString());
        }
        const queryString = params.toString();
        const url = queryString ? `/academies/${academyId}/players/${playerId}/details?${queryString}` : `/academies/${academyId}/players/${playerId}/details`;
        return this.get(url);
    }
}
export const apiClient = new ApiClient();
